<template>
    <AuthLayout>
        <form class="login_form" @submit.prevent="handleLoginAction">
            <div class="gradient_circle"></div>
            <div class="login_desc">
                <strong>{{ $t('pages.auth.greeting.title') }}</strong>
                <p>{{ $t('pages.auth.greeting.description') }}</p>
            </div>
            <div class="login_input_container">
                <div class="login_input_inner">
                    <span class="login_input-name">{{ $t('pages.auth.form.id') }}</span>
                    <div class="input_wrapper">
                        <input
                            v-model="formData.login_id"
                            type="text"
                            :placeholder="$t('pages.auth.form.idPlaceholder')"
                            class="text_input"
                            maxlength="40"
                        />
                    </div>
                    <small v-if="showIdError" class="small_msg-warning right">{{ $t('pages.auth.form.errorId') }}</small>
                </div>
                <div class="login_input_inner">
                    <span class="login_input-name">{{ $t('pages.auth.form.password') }}</span>
                    <div class="input_wrapper">
                        <input
                            v-model="formData.password"
                            :type="showPassword ? 'text' : 'password'"
                            :placeholder="$t('pages.auth.form.passwordPlaceholder')"
                            class="text_input password_input"
                            maxlength="40"
                        />
                        <button type="button" class="hide_btn visible" @click="togglePasswordVisibility">
                            <i :class="['ico_eyes', { open: showPassword }]"></i>
                        </button>
                    </div>
                    <small v-if="showPasswordError" class="small_msg-warning right">{{ $t('pages.auth.form.errorPassword') }}</small>
                </div>
            </div>
            <button type="submit" class="login_btn" :disabled="formData.processing">
                <span>{{ $t('pages.auth.button.login') }}</span>
            </button>
        </form>

        <!-- 에러 모달 -->
        <NoticeModal
            v-if="Object.keys(pageData.props.errors).length"
            v-model:isShow="isModalVisible"
            :title="$t('pages.auth.modal.title')"
            class="auth_error_modal"
        >
            <template v-for="(item, i) in pageData.props.errors" :key="i">
                {{ item }}
            </template>
        </NoticeModal>
    </AuthLayout>
</template>

<script setup lang="ts">
    import { ref, watch } from 'vue';
    import { useForm, usePage } from '@inertiajs/vue3';
    import { PageProps } from '@/admin/types';
    import AuthLayout from '@/teacher/layouts/AuthLayout.vue';
    import NoticeModal from '@/teacher/components/modals/NoticeModal.vue';

    const pageData = usePage<PageProps>();

    /** 비밀번호 표시 여부 */
    const showPassword = ref(false);

    /** 에러 모달 표시 여부 */
    const isModalVisible = ref(false);

    /** 아이디 에러 표시 여부 */
    const showIdError = ref(false);

    /** 비밀번호 에러 표시 여부 */
    const showPasswordError = ref(false);

    const formData = useForm({
        login_id: '',
        password: '',
    });

    /** 비밀번호 표시/숨김 토글 */
    const togglePasswordVisibility = () => {
        showPassword.value = !showPassword.value;
    };

    /** 유효성 검사 */
    const validateForm = () => {
        let isValid = true;

        // 아이디 검사
        if (!formData.login_id.trim()) {
            showIdError.value = true;
            isValid = false;
        } else {
            showIdError.value = false;
        }

        // 비밀번호 검사
        if (!formData.password.trim()) {
            showPasswordError.value = true;
            isValid = false;
        } else {
            showPasswordError.value = false;
        }

        return isValid;
    };

    /** 로그인 처리 */
    const handleLoginAction = () => {
        if (formData.processing) {
            return;
        }

        // 유효성 검사
        if (!validateForm()) {
            return;
        }

        // 로그인 요청
        formData.post(route('teacher.login'), {
            onFinish: () => {
                if (Object.keys(pageData.props.errors).length) {
                    isModalVisible.value = true;
                }
            },
        });
    };

    /** 아이디 입력 시 에러 초기화 */
    watch(
        () => formData.login_id,
        () => {
            if (showIdError.value) {
                showIdError.value = false;
            }
        },
    );

    /** 비밀번호 입력 시 에러 초기화 */
    watch(
        () => formData.password,
        () => {
            if (showPasswordError.value) {
                showPasswordError.value = false;
            }
        },
    );
</script>
